import React, { useCallback, useEffect, useRef, useState } from 'react'
import '../../styles/feedsAndDashboard/FeedsAndDashboard.css'
import '../../styles/feedsAndDashboard/MyTask.css'
import InfiniteScroll from 'react-infinite-scroll-component'
import { format_day } from '../../utils/format_day'
import { getTasks } from '../../server/server'
import { useTranslation } from 'react-i18next'
import { datesAreOnSameDay, getPercentage, notify, sumArray } from '../../utils/util'
import '../../styles/feedsAndDashboard/TaskFeed.css'
import { ReactComponent as SurveyIconNew } from '../../assets/svg/SurveyIconNew.svg'
import { ReactComponent as JournalBook } from '../../assets/svg/JournalBook.svg'
import { ReactComponent as NoTasksMascot } from '../../assets/svg/NoTasksMascot.svg'
import { format } from 'date-fns'
import SurveyTask from '../dashboard/SurveyTask'
import JournalTask from '../dashboard/JournalTask'
import SingleQuestion from './SingleQuestion'
import SinglePoll from './SinglePoll'
import ColorfulProgressBar from '../ColorfulProgressBar'
import BigImageModal from '../modals/BigImageModal'
import Loading from '../Loading'
import i18n from '../../i18n'
import TasksListSkeleton from '../dashboard/skeleton/TasksListSkeleton'
import { STAGGER_CAP } from '../content/ToGo/ContentCard'

const SCROLL_ID = 'scroll-inf-task'
const TITLE_STYLE = { wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }

const sameIds = (a, b) => a.length === b.length && a.every((task, i) => task?.id === b[i]?.id)

function OpenTask({ user, onCountChange, checkUsersFeedbackNeeded }) {
    const { t } = useTranslation()
    const [openTasks, setOpenTasks] = useState([])
    const [resultsNum, setResultsNum] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    // Bumped only when page 0 comes back with a different set, so the entrance
    // stagger replays on real changes and not on every refetch.
    const [listVersion, setListVersion] = useState(0)

    const [surveyModalOpen, setSurveyModalOpen] = useState(false)
    const [selectedSurvey, setSelectedSurvey] = useState({})
    const [journalTaskModalOpen, setJournalTaskModalOpen] = useState(false)
    const [selectedJournalTask, setSelectedJournalTask] = useState({})
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')

    const requestRef = useRef(0)
    const mountedRef = useRef(true)
    const tasksRef = useRef(openTasks)
    tasksRef.current = openTasks
    const onCountChangeRef = useRef(onCountChange)
    onCountChangeRef.current = onCountChange

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const openBigImage = useCallback((url) => {
        setSelectedImage(url)
        setImageModalOpen(true)
    }, [])
    const closeImageModal = useCallback(() => {
        setImageModalOpen(false)
        setSelectedImage('')
    }, [])

    const closeSurveyModal = () => {
        setSelectedSurvey({})
        setSurveyModalOpen(false)
    }
    const selectSurvey = (item) => {
        setSelectedSurvey(item)
        setSurveyModalOpen(true)
    }

    const openJournalTaskModal = (journalTask) => {
        setJournalTaskModalOpen(true)
        setSelectedJournalTask(journalTask)
    }
    const closeJournalTaskModal = () => {
        setJournalTaskModalOpen(false)
        setSelectedJournalTask({})
    }

    const userId = user?.id
    const loadTasks = useCallback(
        async (number = resultsNum) => {
            const requestId = ++requestRef.current
            const isCurrent = () => mountedRef.current && requestId === requestRef.current
            const response = await getTasks({ user_id: userId, number_of_tasks: number })
            if (!isCurrent()) return
            if (response?.status === 200) {
                const tasks = Array.isArray(response.data?.tasks) ? response.data.tasks : []
                const size = Number(response.data?.size) || 0
                if (number === 0) {
                    if (!sameIds(tasksRef.current, tasks)) setListVersion((v) => v + 1)
                    setOpenTasks(tasks)
                    setHasMore(tasks.length > 0 && size > tasks.length)
                    onCountChangeRef.current?.(size)
                } else {
                    const merged = [...tasksRef.current, ...tasks]
                    setOpenTasks(merged)
                    setHasMore(tasks.length > 0 && size > merged.length)
                }
            } else {
                // Keep whatever is on screen; just stop asking for more.
                setHasMore(false)
                notify(t('yourDataIsNotUpdated'), 'error')
            }
            setLoading(false)
        },
        [userId, resultsNum, t]
    )

    useEffect(() => {
        loadTasks()
    }, [loadTasks])

    const isEmpty = !loading && openTasks.length === 0

    return (
        <div id={SCROLL_ID} className="px-6 mb-5 grey-small-scroll overflow-y-scroll xxs:max-h-[70vh] xl:max-h-[100vh] xl:h-0 xl:flex-auto">
            {loading && openTasks.length === 0 && <TasksListSkeleton />}
            {isEmpty && (
                <div className="no-tasks-container dsh-empty">
                    <div className="flex items-center justify-center pt-[60px] -mx-[12px]">
                        <NoTasksMascot />
                    </div>
                    <div className="flex-1">
                        <p className="no-tasks-title">{t('congratulations')} !</p>
                        <p className="no-tasks-subtitle">{t('youHaveCompletedAllYourTasks')} </p>
                    </div>
                </div>
            )}
            <InfiniteScroll
                dataLength={openTasks.length}
                next={() => setResultsNum(openTasks.length)}
                hasMore={hasMore}
                scrollableTarget={SCROLL_ID}
                inverse={false}
                loader={
                    <div className="flex justify-center py-4">
                        <Loading primaryColor="#5E639B" secondaryColor="#DEDCF6" size={24} stroke={6} />
                    </div>
                }
            >
                <div className="dsh-list-animate" key={listVersion}>
                    {openTasks.map((task, index) => {
                        const time = format(new Date(task.publicationDate), 'HH:mm')
                        const previousNoteDay = index > 0 ? openTasks[index - 1]?.publicationDate : null
                        const sameDay = !!previousNoteDay && datesAreOnSameDay(new Date(task.publicationDate), new Date(previousNoteDay))
                        const doneQuestions = sumArray(task?.sections?.map((section) => section?.questions?.filter((e) => !!e?.answer)?.length))
                        const totalQuestions = sumArray(task?.sections?.map((section) => section?.questions?.length))
                        return (
                            <div className="dsh-row flex flex-col items-center" key={task.id} style={{ '--i': Math.min(index, STAGGER_CAP) }}>
                                {!sameDay && (
                                    <div className="mt-[15px]" style={{ height: 20 }}>
                                        <span className="text-[14px] leading-5 font-medium text-darkPurple opacity-60">{t('date')}: </span>
                                        <span className="text-[14px] leading-5 font-medium text-darkPurple">{format_day(task.publicationDate, false, i18n.language)}</span>
                                    </div>
                                )}
                                {task.type === 'question' && (
                                    <SingleQuestion
                                        sameDay={sameDay}
                                        task={task}
                                        user={user}
                                        setResultsNum={setResultsNum}
                                        resultsNum={resultsNum}
                                        loadTasks={loadTasks}
                                        openBigImage={openBigImage}
                                        checkUsersFeedbackNeeded={checkUsersFeedbackNeeded}
                                    />
                                )}
                                {task.type === 'poll' && (
                                    <SinglePoll
                                        sameDay={sameDay}
                                        task={task}
                                        user={user}
                                        setResultsNum={setResultsNum}
                                        resultsNum={resultsNum}
                                        loadTasks={loadTasks}
                                        openBigImage={openBigImage}
                                        checkUsersFeedbackNeeded={checkUsersFeedbackNeeded}
                                    />
                                )}
                                {task.type === 'journal' && (
                                    <div
                                        className="w-full p-5 my-2 bg-[#F8F8FB] rounded-[10px] cursor-pointer hover:bg-[#ECEBF8] transition-colors"
                                        onClick={() => openJournalTaskModal(task)}
                                    >
                                        <div className="flex flex-row items-center">
                                            <JournalBook />
                                            <p className="ml-[10px] text-[16px] font-[500] text-[#413F5E]">{t('open_journal')}</p>
                                            <div className="flex-1"></div>
                                            <div className="text-[14px] font-[500] text-[#413F5E] opacity-60 mr-[5px]">{t('time')}:</div>
                                            <div className="text-[14px] font-[500] text-[#413F5E]">{time}</div>
                                        </div>
                                        <div className="mt-[15px] text-[18px] font-bold text-[#413F5E]" style={TITLE_STYLE}>
                                            {task.title}
                                        </div>
                                    </div>
                                )}
                                {task.type === 'survey' && (
                                    <div
                                        className="w-full p-5 my-2 bg-containerGray rounded-[10px] cursor-pointer hover:bg-[#ECEBF8] transition-colors"
                                        onClick={() => selectSurvey(task)}
                                    >
                                        <div className="flex flex-row items-center">
                                            <SurveyIconNew />
                                            <p className="ml-[10px] text-[16px] font-[500] text-[#413F5E]">{task.anonymous ? t('anonymous_survey') : t('open_survey')}</p>
                                            <div className="flex-1"></div>
                                            <div className="text-[14px] font-[500] text-[#413F5E] opacity-60 mr-[5px]">{t('time')}:</div>
                                            <div className="text-[14px] font-[500] text-[#413F5E]">{time}</div>
                                        </div>
                                        <div className="mt-[15px] text-[18px] font-bold text-[#413F5E]" style={TITLE_STYLE}>
                                            {task.title}
                                        </div>
                                        {doneQuestions > 0 && (
                                            <div className="flex flex-row items-center mt-[10px]">
                                                <ColorfulProgressBar percentage={getPercentage(doneQuestions, totalQuestions)} />
                                                <label className="text-[#FBA75A] font-bold text-[16px] ml-[9px]">
                                                    {getPercentage(doneQuestions, totalQuestions)}% {t('completed')}
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </InfiniteScroll>
            <SurveyTask
                modalIsOpen={surveyModalOpen}
                closeModal={closeSurveyModal}
                item={selectedSurvey}
                completed={loadTasks}
                user={user}
                checkUsersFeedbackNeeded={checkUsersFeedbackNeeded}
            />
            <JournalTask
                modalIsOpen={journalTaskModalOpen}
                closeModal={closeJournalTaskModal}
                task={selectedJournalTask}
                openImageBig={openBigImage}
                checkUsersFeedbackNeeded={checkUsersFeedbackNeeded}
            />
            <BigImageModal modalIsOpen={imageModalOpen} closeModal={closeImageModal} selectedImage={selectedImage} />
        </div>
    )
}

export default OpenTask
